import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { AddressAutocomplete } from '@/components/ui/address-autocomplete';
import {
  Loader2, Clock, Truck, Zap, TrendingDown, User, Check, AlertTriangle, X, FileCheck, Timer,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

function formatDate(str) {
  const d = new Date(str + 'T00:00:00');
  return d.toLocaleDateString('ru-RU', { weekday: 'short', day: 'numeric', month: 'short' });
}

function formatDuration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

const DURATION_PRESETS = [
  { label: '1h', minutes: 60 },
  { label: '1h 30m', minutes: 90 },
  { label: '2h', minutes: 120 },
  { label: '2h 30m', minutes: 150 },
  { label: '3h', minutes: 180 },
  { label: '4h', minutes: 240 },
];

// ====== DURATION INPUT ======
function DurationInput({ hours, minutes, onHoursChange, onMinutesChange }) {
  const total = hours * 60 + minutes;
  const isInvalid = total < 15 || total > 720;

  return (
    <div data-testid="duration-input-group" className="w-full sm:w-auto">
      <Label className="text-xs">Duration</Label>
      <div className="flex items-center gap-1.5">
        <Select value={String(hours)} onValueChange={v => onHoursChange(parseInt(v))}>
          <SelectTrigger className="h-10 sm:h-8 text-base sm:text-xs w-[80px] sm:w-[70px]" data-testid="duration-hours-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 13 }, (_, i) => (
              <SelectItem key={i} value={String(i)} className="text-sm sm:text-xs">{i}h</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(minutes)} onValueChange={v => onMinutesChange(parseInt(v))}>
          <SelectTrigger className="h-10 sm:h-8 text-base sm:text-xs w-[80px] sm:w-[70px]" data-testid="duration-minutes-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {[0, 15, 30, 45].map(m => (
              <SelectItem key={m} value={String(m)} className="text-sm sm:text-xs">{m}m</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      {isInvalid && (
        <p className="text-[10px] text-red-400 mt-0.5" data-testid="duration-validation-error">
          {total < 15 ? 'Min 15 min' : 'Max 12h'}
        </p>
      )}
    </div>
  );
}

// ====== DURATION PRESETS ======
function DurationPresets({ onSelect, activeMinutes }) {
  return (
    <div className="flex flex-wrap gap-1" data-testid="duration-presets">
      {DURATION_PRESETS.map(p => (
        <Button
          key={p.minutes}
          variant={activeMinutes === p.minutes ? "default" : "outline"}
          size="sm"
          className={cn("h-6 text-[10px] px-2", activeMinutes === p.minutes && "bg-primary")}
          onClick={() => onSelect(p.minutes)}
          data-testid={`preset-${p.minutes}`}
        >
          {p.label}
        </Button>
      ))}
    </div>
  );
}

// ====== CANDIDATE CARD ======
function CandidateCard({ c, index, isBestFit, isNearFit, onSelect, selecting }) {
  const isFirst = index === 0;
  return (
    <div
      className={cn(
        'p-3 rounded-lg border transition-all cursor-pointer',
        isFirst && !isNearFit ? 'border-primary/40 bg-primary/5 hover:border-primary/70' : '',
        isNearFit ? 'border-orange-500/60 bg-orange-500/8 hover:border-orange-500/90 ring-1 ring-orange-500/20' : 'border-border hover:border-primary/50',
      )}
      onClick={() => onSelect(c)}
      data-testid={`candidate-card-${isNearFit ? 'nf' : isBestFit ? 'bf' : 'f'}-${index}`}
    >
      {/* Header row */}
      <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
        {isFirst && !isNearFit && (
          <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 text-[10px] px-1.5">
            {isBestFit ? 'Best fit' : 'Fastest'}
          </Badge>
        )}
        {isNearFit && (
          <Badge className="bg-orange-600/25 text-orange-400 border-orange-500/50 text-[10px] px-1.5 font-semibold" data-testid="near-fit-badge">
            ⚠ Сокращённый
          </Badge>
        )}
        {c.is_remote && <Badge variant="outline" className="text-[10px] text-orange-400 border-orange-400/30">Remote</Badge>}
        {c.rush_hour && <Badge variant="outline" className="text-[10px] text-red-400 border-red-400/30">Час пик</Badge>}
        {c.is_preferred_worker && <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 text-[10px] px-1.5">★ Знакомый</Badge>}
        <span className="font-medium text-sm truncate max-w-[120px] sm:max-w-none">{c.worker_name}</span>
        <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: c.worker_color }} />
        <span className="ml-auto text-xs text-muted-foreground whitespace-nowrap">{formatDate(c.date)}</span>
      </div>

      {/* Info row */}
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{c.slot_start} - {c.slot_end}</span>
        <span className="flex items-center gap-1"><Truck className="h-3 w-3" />{c.travel_before_minutes}m</span>
        <span>load {c.projected_load_percent}%</span>
        {isBestFit && !isNearFit && c.best_fit_score != null && <span className="font-mono text-primary">score {c.best_fit_score}</span>}
        {c.detour_factor > 2.0 && <span className="text-orange-400 text-[10px]">объезд ×{c.detour_factor}</span>}
      </div>

      {isNearFit && (
        <div className="mt-2 p-2 rounded bg-orange-500/12 border border-orange-500/40 text-xs space-y-1" data-testid="near-fit-info">
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            <span>Запрошено: <b>{formatDuration(c.requested_duration_minutes)}</b></span>
            <span>Доступно: <b className="text-orange-400">{formatDuration(c.available_work_minutes)}</b></span>
            <span className="text-red-400 font-bold">−{Math.round(c.duration_deficit_minutes)} мин</span>
            {c.is_gap_filler && <span className="text-emerald-400 font-medium border border-emerald-500/30 rounded px-1">Между заказами</span>}
          </div>
          <p className="text-orange-400 font-medium flex items-center gap-1">
            <AlertTriangle className="h-3 w-3 flex-shrink-0" />
            Уборка будет короче запрошенного времени — требуется подтверждение
          </p>
        </div>
      )}

      {c.explanation && !isNearFit && <p className="text-[10px] text-muted-foreground mt-1 italic truncate">{c.explanation}</p>}
      {selecting && <Loader2 className="h-3 w-3 animate-spin mt-1" />}
    </div>
  );
}

// ====== NEAR-FIT CONFIRM DIALOG ======
function NearFitConfirmDialog({ open, onOpenChange, candidate, onConfirm, loading }) {
  if (!candidate) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="near-fit-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-amber-400">
            <AlertTriangle className="h-5 w-5" /> Shorter Slot Confirmation
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            The available slot is shorter than the requested duration
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3 p-3 rounded-lg bg-muted/50 text-sm">
            <div>
              <p className="text-xs text-muted-foreground">Requested</p>
              <p className="font-semibold">{formatDuration(candidate.requested_duration_minutes)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Available</p>
              <p className="font-semibold text-amber-400">{formatDuration(candidate.available_work_minutes)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Shorter by</p>
              <p className="font-semibold text-orange-400">{Math.round(candidate.duration_deficit_minutes)} min</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Worker</p>
              <p className="font-semibold">{candidate.worker_name}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Date</p>
              <p className="font-semibold">{formatDate(candidate.date)}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Time</p>
              <p className="font-semibold">{candidate.slot_start} - {candidate.slot_end}</p>
            </div>
          </div>
          <div className="p-2 rounded border border-amber-500/30 bg-amber-500/10 text-xs text-amber-400">
            By confirming, you acknowledge this slot is shorter than requested and agree to offer the client a reduced time window.
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="near-fit-cancel-btn">Cancel</Button>
          <Button onClick={onConfirm} disabled={loading}
            className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="near-fit-confirm-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Check className="h-4 w-4 mr-2" />}
            Confirm Shorter Slot
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ====== MANUAL ASSIGN PANEL ======
function ManualAssignPanel({ api, draftId, workers, onSelected }) {
  const [workerId, setWorkerId] = useState('');
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [validation, setValidation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selecting, setSelecting] = useState(false);

  const handleValidate = async () => {
    if (!workerId || !date || !time) return toast.error('Fill all fields');
    setLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draftId}/manual-validate`, {
        worker_id: workerId, date, time_start: time,
      });
      setValidation(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Validation failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelect = async () => {
    setSelecting(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draftId}/manual-select`, {
        worker_id: workerId, date, time_start: time,
      });
      toast.success('Slot locked');
      onSelected(resp.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Selection failed');
    } finally {
      setSelecting(false);
    }
  };

  return (
    <div className="space-y-3" data-testid="manual-assign-panel">
      <div className="grid grid-cols-3 gap-2">
        <div>
          <Label className="text-xs">Worker</Label>
          <Select value={workerId} onValueChange={setWorkerId}>
            <SelectTrigger className="h-8 text-xs" data-testid="manual-worker-select"><SelectValue placeholder="Select..." /></SelectTrigger>
            <SelectContent>
              {workers.map(w => <SelectItem key={w.id} value={w.id} className="text-xs">{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} className="h-8 text-xs" data-testid="manual-date-input" />
        </div>
        <div>
          <Label className="text-xs">Time</Label>
          <Input type="time" value={time} onChange={e => setTime(e.target.value)} className="h-8 text-xs" data-testid="manual-time-input" />
        </div>
      </div>
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={handleValidate} disabled={loading} data-testid="manual-validate-btn">
          {loading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Check className="h-3 w-3 mr-1" />}Validate
        </Button>
        {validation && validation.status !== 'conflict' && (
          <Button size="sm" onClick={handleSelect} disabled={selecting} data-testid="manual-select-btn">
            {selecting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}Lock Slot
          </Button>
        )}
      </div>
      {validation && (
        <div className={cn(
          'p-2 rounded text-xs border',
          validation.status === 'valid' ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400' :
          validation.status === 'warning' ? 'border-amber-500/30 bg-amber-500/10 text-amber-400' :
          'border-red-500/30 bg-red-500/10 text-red-400',
        )} data-testid="manual-validation-result">
          <div className="flex items-center gap-1 font-medium mb-1">
            {validation.status === 'valid' ? <Check className="h-3 w-3" /> :
             validation.status === 'warning' ? <AlertTriangle className="h-3 w-3" /> :
             <X className="h-3 w-3" />}
            {validation.status.toUpperCase()}
          </div>
          <p>{validation.reason}</p>
          {validation.details && (
            <div className="flex gap-3 mt-1 text-[10px] text-muted-foreground">
              <span>travel: {validation.details.travel_before_minutes}m + {validation.details.travel_after_minutes}m</span>
              <span>load: {validation.details.projected_load_percent}%</span>
              <span>leftover: {validation.details.leftover_gap_minutes}m</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ====== COMPLETE ORDER DIALOG ======
function CompleteOrderDialog({ open, onOpenChange, api, draftId, draft, onCompleted }) {
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [clientId, setClientId] = useState('');
  const [selectedServiceIds, setSelectedServiceIds] = useState([]);
  const [description, setDescription] = useState('');
  const [totalPrice, setTotalPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [showNewClient, setShowNewClient] = useState(false);
  const [showPriceConfirm, setShowPriceConfirm] = useState(false);
  const [newClient, setNewClient] = useState({ name: '', phone: '', email: '', address: '', lat: null, lng: null });
  const [creatingClient, setCreatingClient] = useState(false);
  const [clientSearch, setClientSearch] = useState('');

  useEffect(() => {
    if (open && draft) {
      // Reset state first
      setClientId('');
      setSelectedServiceIds([]);
      setDescription('');
      setTotalPrice('');
      setShowNewClient(false);
      setShowPriceConfirm(false);
      
      // Pre-fill from draft
      const clientName = draft.title || '';
      setClientSearch(clientName);
      
      // Pre-fill new client form with draft data
      setNewClient({
        name: clientName,
        phone: '',
        email: '',
        address: draft.address_full || '',
        lat: draft.address_lat || null,
        lng: draft.address_lng || null
      });
      
      // Pre-fill service type if available
      if (draft.service_type_id) {
        setSelectedServiceIds([draft.service_type_id]);
      }
      
      // Load clients and try to find match
      api.get('/clients?limit=200').then(r => {
        const clientsList = Array.isArray(r.data) ? r.data : [];
        setClients(clientsList);
        
        // Try to find exact match by name
        if (clientName) {
          const exactMatch = clientsList.find(c => c.name?.toLowerCase() === clientName.toLowerCase());
          if (exactMatch) {
            setClientId(exactMatch.id);
            setClientSearch(exactMatch.name);
          }
        }
      }).catch(() => {});
      
      api.get('/service-types').then(r => setServices(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [open, api, draft]);
  
  // Filter clients based on search
  const filteredClients = clients.filter(c => 
    !clientSearch || c.name?.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const handleCreateClient = async () => {
    // Frontend validation before sending
    if (!newClient.name || newClient.name.trim().length < 2) {
      return toast.error('Имя: минимум 2 символа');
    }
    if (!newClient.phone || newClient.phone.replace(/\D/g, '').length < 10) {
      return toast.error('Телефон: минимум 10 цифр');
    }
    if (!newClient.email || !newClient.email.includes('@') || newClient.email.length < 5) {
      return toast.error('Email: введите корректный email с @');
    }
    if (!newClient.address || newClient.address.trim().length < 5) {
      return toast.error('Адрес: минимум 5 символов');
    }
    
    setCreatingClient(true);
    try {
      const resp = await api.post('/clients', {
        name: newClient.name,
        phone: newClient.phone,
        email: newClient.email,
        address: { 
          full: newClient.address,
          lat: newClient.lat,
          lng: newClient.lng,
        },
      });
      const created = resp.data;
      setClients(prev => [created, ...prev]);
      setClientId(created.id);
      setClientSearch(created.name);
      setShowNewClient(false);
      setNewClient({ name: '', phone: '', email: '', address: '', lat: null, lng: null });
      toast.success(`Клиент "${created.name}" создан`);
    } catch (err) {
      // Handle validation errors from backend
      const detail = err.response?.data?.detail;
      if (detail?.errors && Array.isArray(detail.errors)) {
        toast.error(detail.errors.join('\n'));
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Ошибка при создании клиента');
      }
    } finally {
      setCreatingClient(false);
    }
  };

  const addService = (id) => {
    if (id && !selectedServiceIds.includes(id)) {
      setSelectedServiceIds(prev => [...prev, id]);
    }
  };
  const removeService = (id) => {
    setSelectedServiceIds(prev => prev.filter(s => s !== id));
  };

  const handleSubmit = () => {
    if (!clientId) return toast.error('Select a client');
    setShowPriceConfirm(true);
  };

  const handleConfirmOrder = async () => {
    setShowPriceConfirm(false);
    setLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draftId}/complete-order`, {
        client_id: clientId,
        service_type_ids: selectedServiceIds.length > 0 ? selectedServiceIds : undefined,
        description,
        total_price: parseFloat(totalPrice) || 0,
        address_street: draft?.address_full || '',
      });
      toast.success('Order created successfully');
      onCompleted(resp.data);
      onOpenChange(false);
    } catch (err) {
      // FE-01 FIX: More specific error message based on error type
      console.error('Create order error:', err);
      const errorMsg = err.response?.data?.detail;
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        toast.error('Request timed out. Please check if the order was created.');
      } else if (!err.response) {
        toast.error('Network error. Please check your connection and verify if order was created.');
      } else {
        toast.error(errorMsg || 'Failed to create order');
      }
    } finally {
      setLoading(false);
    }
  };

  const isNearFit = draft?.fit_type === 'near';
  const priceNum = parseFloat(totalPrice) || 0;

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100vw-32px)] max-w-md max-h-[85vh] overflow-y-auto p-4 sm:p-6" data-testid="complete-order-dialog">
        <DialogHeader className="pb-2">
          <DialogTitle className="flex items-center gap-2 text-base">
            <FileCheck className="h-4 w-4" /> Complete Order
          </DialogTitle>
        </DialogHeader>
        {draft && (
          <div className="p-2 rounded bg-muted/50 text-xs space-y-1 mb-2">
            <div className="font-medium truncate">{draft.title}</div>
            <div className="text-muted-foreground">
              {draft.locked_worker_id && <span>Worker locked</span>}
              {draft.locked_date && <span> | {formatDate(draft.locked_date)}</span>}
              {draft.locked_time_start && <span> {draft.locked_time_start}-{draft.locked_time_end}</span>}
            </div>
            {isNearFit && (
              <div className="mt-1 p-1.5 rounded bg-amber-500/10 border border-amber-500/20 text-amber-400">
                Near-fit: {formatDuration(draft.selected_duration_minutes || draft.duration_minutes)} (requested {formatDuration(draft.requested_duration_minutes || draft.duration_minutes)})
              </div>
            )}
          </div>
        )}
        <div className="space-y-3">
          {/* CLIENT SECTION */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <Label className="text-xs">Client *</Label>
              <Button variant="ghost" size="sm" className="h-6 text-xs text-blue-400 hover:text-blue-300 px-2"
                onClick={() => setShowNewClient(!showNewClient)} data-testid="toggle-new-client">
                {showNewClient ? 'Cancel' : '+ New'}
              </Button>
            </div>
            {showNewClient ? (
              <div className="space-y-2 p-2 rounded border border-blue-500/20 bg-blue-500/5">
                <Input placeholder="Name (Имя Фамилия) *" value={newClient.name} onChange={e => setNewClient(p => ({...p, name: e.target.value}))} className="h-10 text-base" data-testid="new-client-name" />
                <Input placeholder="Phone: +1 604 123 4567 *" value={newClient.phone} onChange={e => setNewClient(p => ({...p, phone: e.target.value}))} className="h-10 text-base" data-testid="new-client-phone" type="tel" />
                <Input placeholder="Email *" value={newClient.email} onChange={e => setNewClient(p => ({...p, email: e.target.value}))} className="h-10 text-base" data-testid="new-client-email" type="email" />
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Address *</Label>
                  <AddressAutocomplete
                    value={newClient.address}
                    onChange={(val) => setNewClient(p => ({...p, address: val}))}
                    onSelect={(result) => setNewClient(p => ({
                      ...p, 
                      address: result.address, 
                      lat: result.lat, 
                      lng: result.lng
                    }))}
                    placeholder="Start typing address..."
                    className="h-10 text-base"
                    data-testid="new-client-address"
                  />
                  {newClient.lat && (
                    <p className="text-[10px] text-emerald-400 px-1">✓ Address verified</p>
                  )}
                </div>
                <Button size="sm" className="w-full h-10" onClick={handleCreateClient} 
                  disabled={creatingClient || !newClient.name || !newClient.phone || !newClient.email || !newClient.address} 
                  data-testid="create-client-btn">
                  {creatingClient ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Create Client
                </Button>
              </div>
            ) : (
              <div className="relative">
                <Input
                  type="text"
                  placeholder="Type to search clients..."
                  value={clientSearch}
                  onChange={e => {
                    setClientSearch(e.target.value);
                    if (clientId) setClientId(''); // Clear selection when typing
                  }}
                  className="h-10 text-base"
                  data-testid="complete-client-search"
                />
                {clientId && (
                  <div className="mt-1 p-2 rounded bg-green-500/10 border border-green-500/20 flex items-center justify-between">
                    <span className="text-sm font-medium text-green-400">
                      ✓ {clients.find(c => c.id === clientId)?.name}
                    </span>
                    <button onClick={() => { setClientId(''); setClientSearch(''); }} className="text-xs text-muted-foreground hover:text-red-400">
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
                {!clientId && clientSearch && filteredClients.length > 0 && (
                  <div className="absolute z-50 mt-1 w-full bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {filteredClients.slice(0, 10).map(c => (
                      <button
                        key={c.id}
                        onClick={() => {
                          setClientId(c.id);
                          setClientSearch(c.name);
                        }}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted border-b last:border-b-0"
                      >
                        {c.name}
                        {c.address?.full && <span className="text-xs text-muted-foreground ml-2">({c.address.full.slice(0, 30)}...)</span>}
                      </button>
                    ))}
                  </div>
                )}
                {!clientId && clientSearch && filteredClients.length === 0 && (
                  <div className="mt-1 p-2 text-xs text-muted-foreground">
                    No clients found. Click "+ New" to create one.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SERVICES SECTION */}
          <div>
            <Label className="text-xs mb-1 block">Services</Label>
            {selectedServiceIds.length > 0 && (
              <div className="flex flex-wrap gap-1 mb-2">
                {selectedServiceIds.map(sid => {
                  const svc = services.find(s => s.id === sid);
                  return (
                    <span key={sid} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-primary/10 border border-primary/20">
                      {svc?.name || sid}
                      <button onClick={() => removeService(sid)} className="hover:text-red-400 ml-0.5">&times;</button>
                    </span>
                  );
                })}
              </div>
            )}
            <Select value="" onValueChange={addService}>
              <SelectTrigger className="h-10 text-base" data-testid="complete-service-select">
                <SelectValue placeholder="+ Add service..." />
              </SelectTrigger>
              <SelectContent>
                {services.filter(s => !selectedServiceIds.includes(s.id)).map(s => (
                  <SelectItem key={s.id} value={s.id} className="text-sm">{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* DESCRIPTION */}
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea value={description} onChange={e => setDescription(e.target.value)}
              placeholder="Order details..." className="text-base min-h-[60px]" data-testid="complete-description" />
          </div>

          {/* TOTAL PRICE */}
          <div>
            <Label className="text-xs">Total Price ($)</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" value={totalPrice} onChange={e => setTotalPrice(e.target.value)}
              placeholder="0.00" className="h-10 text-base font-medium" data-testid="complete-total-price" />
            {(totalPrice === '' || priceNum === 0) && (
              <p className="text-xs text-amber-400 mt-1">If price is $0, a reminder will appear on the dashboard</p>
            )}
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2 pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="w-full sm:w-auto h-10">Cancel</Button>
          <Button onClick={handleSubmit} disabled={loading || !clientId} className="w-full sm:w-auto h-10" data-testid="complete-order-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileCheck className="h-4 w-4 mr-2" />}
            Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* PRICE CONFIRMATION DIALOG */}
    <Dialog open={showPriceConfirm} onOpenChange={setShowPriceConfirm}>
      <DialogContent className="max-w-sm" data-testid="price-confirm-dialog">
        <DialogHeader>
          <DialogTitle className="text-center">Confirm Price</DialogTitle>
        </DialogHeader>
        <div className="text-center py-4 space-y-3">
          <p className="text-sm text-muted-foreground">Please verify the order total:</p>
          <div className="text-5xl font-bold tracking-tight" style={{ color: priceNum > 0 ? '#22c55e' : '#ef4444' }} data-testid="price-confirm-amount">
            ${priceNum.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          {priceNum === 0 && <p className="text-amber-400 text-sm font-medium">Price is $0 — a dashboard reminder will be created</p>}
          <p className="text-sm text-muted-foreground">Is this the correct amount?</p>
        </div>
        <DialogFooter className="flex gap-2 sm:justify-center">
          <Button variant="outline" className="flex-1" onClick={() => setShowPriceConfirm(false)} data-testid="price-confirm-no">
            No, Edit Price
          </Button>
          <Button className="flex-1 bg-green-600 hover:bg-green-700" onClick={handleConfirmOrder} disabled={loading} data-testid="price-confirm-yes">
            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Yes, Create Order
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}

// ====== MAIN PANEL ======
export function AvailabilityCheckPanel({ api, onDraftCreated }) {
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [addressCoords, setAddressCoords] = useState({ lat: null, lng: null });
  const [durationHours, setDurationHours] = useState(2);
  const [durationMinutes, setDurationMinutes] = useState(0);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState(null);
  const [workers, setWorkers] = useState([]);
  const [selecting, setSelecting] = useState(null);
  const [selectedLock, setSelectedLock] = useState(null);
  const [completeOpen, setCompleteOpen] = useState(false);
  // Near-fit confirm dialog
  const [nearFitConfirmOpen, setNearFitConfirmOpen] = useState(false);
  const [nearFitCandidate, setNearFitCandidate] = useState(null);
  const [nearFitLoading, setNearFitLoading] = useState(false);

  const totalDurationMinutes = durationHours * 60 + durationMinutes;
  const isDurationValid = totalDurationMinutes >= 15 && totalDurationMinutes <= 720;

  const handlePresetSelect = (minutes) => {
    setDurationHours(Math.floor(minutes / 60));
    setDurationMinutes(minutes % 60);
  };

  useEffect(() => {
    api.get('/workers?limit=50').then(r => {
      setWorkers(Array.isArray(r.data) ? r.data.map(w => ({ id: w.id, name: `${w.first_name || ''} ${w.last_name || ''}`.trim() || w.id })) : []);
    }).catch(() => {});
    
    // Restore last draft if exists (within 1 hour TTL)
    api.get('/schedule/drafts/my-latest').then(r => {
      if (r.data && r.data.status !== 'completed') {
        setDraft(r.data);
        setTitle(r.data.title || '');
        setAddress(r.data.address_full || '');
        if (r.data.address_lat && r.data.address_lng) {
          setAddressCoords({ lat: r.data.address_lat, lng: r.data.address_lng });
        }
        if (r.data.duration_minutes) {
          setDurationHours(Math.floor(r.data.duration_minutes / 60));
          setDurationMinutes(r.data.duration_minutes % 60);
        }
      }
    }).catch(() => {});
  }, [api]);

  const handleCheck = async () => {
    if (!title) return toast.error('Client name required');
    if (!isDurationValid) return toast.error('Duration must be between 15 min and 12 hours');
    setLoading(true);
    setDraft(null);
    setSelectedLock(null);
    try {
      const payload = {
        title, 
        address_full: address, 
        duration_minutes: totalDurationMinutes,
      };
      // If we have coords from autocomplete, include them
      if (addressCoords.lat && addressCoords.lng) {
        payload.service_lat = addressCoords.lat;
        payload.service_lng = addressCoords.lng;
      }
      const resp = await api.post('/schedule/check-availability', payload);
      setDraft(resp.data);
      if (resp.data.geocoding_warning) {
        toast.warning(resp.data.geocoding_warning);
      }
      onDraftCreated?.();
    } catch (err) {
      toast.error('Check failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCandidate = async (candidate, mode) => {
    if (!draft) return;

    // Near-fit: open confirm dialog first
    if (candidate.fit_type === 'near') {
      setNearFitCandidate(candidate);
      setNearFitConfirmOpen(true);
      return;
    }

    setSelecting(candidate.slot_start + candidate.date);
    try {
      const resp = await api.post(`/schedule/drafts/${draft.draft_id}/select-slot`, {
        mode, candidate,
      });
      setSelectedLock(resp.data);
      setDraft(prev => prev ? { ...prev, status: 'slot_selected',
        locked_worker_id: resp.data.worker_id,
        locked_date: resp.data.date,
        locked_time_start: resp.data.time_start,
        locked_time_end: resp.data.time_end,
        fit_type: resp.data.fit_type,
        selected_duration_minutes: resp.data.selected_duration_minutes,
        requested_duration_minutes: resp.data.requested_duration_minutes,
        title: resp.data.title || prev.title,
        address_full: resp.data.address_full || prev.address_full,
        address_lat: resp.data.address_lat || prev.address_lat,
        address_lng: resp.data.address_lng || prev.address_lng,
      } : prev);
      toast.success(`Slot locked: ${candidate.worker_name} ${candidate.date} ${candidate.slot_start}`);
      onDraftCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Slot unavailable (revalidation failed)');
    } finally {
      setSelecting(null);
    }
  };

  const handleNearFitConfirm = async () => {
    if (!draft || !nearFitCandidate) return;
    setNearFitLoading(true);
    try {
      const resp = await api.post(`/schedule/drafts/${draft.draft_id}/select-slot`, {
        mode: 'near_fit',
        candidate: nearFitCandidate,
        manager_confirmed_shorter_slot: true,
      });
      setSelectedLock(resp.data);
      setDraft(prev => prev ? { ...prev, status: 'slot_selected',
        locked_worker_id: resp.data.worker_id,
        locked_date: resp.data.date,
        locked_time_start: resp.data.time_start,
        locked_time_end: resp.data.time_end,
        fit_type: resp.data.fit_type,
        selected_duration_minutes: resp.data.selected_duration_minutes,
        requested_duration_minutes: resp.data.requested_duration_minutes,
        title: resp.data.title || prev.title,
        address_full: resp.data.address_full || prev.address_full,
        address_lat: resp.data.address_lat || prev.address_lat,
        address_lng: resp.data.address_lng || prev.address_lng,
      } : prev);
      toast.success(`Near-fit slot locked: ${nearFitCandidate.worker_name} ${nearFitCandidate.date} ${nearFitCandidate.slot_start}`);
      setNearFitConfirmOpen(false);
      setNearFitCandidate(null);
      onDraftCreated?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Slot unavailable');
    } finally {
      setNearFitLoading(false);
    }
  };

  const handleManualSelected = (lockData) => {
    setSelectedLock({ lock_expires_at: new Date(Date.now() + 3600000).toISOString(), ...lockData });
    setDraft(prev => prev ? { ...prev, status: 'slot_selected' } : prev);
    onDraftCreated?.();
  };

  const handleDiscard = async () => {
    if (!draft) return;
    try {
      await api.delete(`/schedule/drafts/${draft.draft_id}`);
      setDraft(null);
      setSelectedLock(null);
      toast.success('Draft discarded');
      onDraftCreated?.();
    } catch (err) {
      toast.error('Discard failed');
    }
  };

  const handleOrderCompleted = (data) => {
    // Clear all form data after order is created
    setDraft(null);
    setSelectedLock(null);
    setTitle('');
    setAddress('');
    setAddressCoords({ lat: null, lng: null });
    setDurationHours(2);
    setDurationMinutes(0);
    onDraftCreated?.();
  };

  const isSlotSelected = draft?.status === 'slot_selected' || selectedLock;
  const hasFullFit = draft?.has_full_fit;
  const hasNearFit = draft?.has_near_fit;
  const nearFitResults = draft?.near_fit_results || [];

  // Determine tab count for grid
  const tabCount = 3 + (nearFitResults.length > 0 ? 1 : 0);

  return (
    <>
      <Card className="mb-6" data-testid="availability-check-panel">
        <CardContent className="p-4">
          {/* Header - responsive */}
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Zap className="h-4 w-4 text-primary flex-shrink-0" />
              <span className="font-semibold text-sm">Check Availability</span>
            </div>
            {draft && (
              <Badge variant="outline" className="text-[10px] whitespace-nowrap">Draft: {draft.status}</Badge>
            )}
            {draft?.geocoding_status && draft.geocoding_status !== 'provided' && (
              <Badge variant="outline" className={cn('text-[10px] whitespace-nowrap',
                draft.geocoding_status === 'resolved' ? 'text-emerald-400 border-emerald-500/30' :
                'text-amber-400 border-amber-500/30'
              )}>
                geo: {draft.geocoding_status}
              </Badge>
            )}
            {draft?.service_lat != null && (
              <span className="text-[9px] text-muted-foreground hidden sm:inline">
                ({draft.service_lat?.toFixed(4)}, {draft.service_lng?.toFixed(4)})
              </span>
            )}
            {selectedLock && (
              <Badge className="text-[10px] bg-emerald-500/20 text-emerald-400 border-emerald-500/30 whitespace-nowrap">
                <Clock className="h-3 w-3 mr-1 hidden sm:inline" />
                Locked
              </Badge>
            )}
            <div className="ml-auto flex gap-1 flex-shrink-0">
              {isSlotSelected && (
                <Button size="sm" onClick={() => setCompleteOpen(true)} data-testid="open-complete-order-btn"
                  className="text-xs h-8 sm:h-7 bg-emerald-600 hover:bg-emerald-700 px-2 sm:px-3">
                  <FileCheck className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Complete Order</span>
                </Button>
              )}
              {draft && (
                <Button variant="ghost" size="sm" className="text-xs text-muted-foreground h-8 sm:h-7 px-2" onClick={handleDiscard} data-testid="discard-draft-btn">
                  <X className="h-3 w-3 sm:mr-1" />
                  <span className="hidden sm:inline">Discard</span>
                </Button>
              )}
            </div>
          </div>

          {/* Input form - responsive grid */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto_auto] gap-3 sm:gap-2 mb-2">
            <div>
              <Label className="text-xs">Client Name</Label>
              <Input 
                value={title} 
                onChange={e => setTitle(e.target.value)} 
                placeholder="Client name..." 
                className="h-10 sm:h-8 text-base sm:text-xs" 
                data-testid="avail-title-input" 
              />
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <AddressAutocomplete
                value={address}
                onChange={setAddress}
                onSelect={(result) => {
                  setAddress(result.address);
                  setAddressCoords({ lat: result.lat, lng: result.lng });
                }}
                placeholder="Start typing address..."
                className="h-10 sm:h-8 text-base sm:text-xs"
                data-testid="avail-address-input"
              />
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {addressCoords.lat ? (
                  <span className="text-emerald-400">✓ Address selected</span>
                ) : (
                  <span className="sm:hidden">Type address for suggestions</span>
                )}
              </p>
            </div>
            <DurationInput
              hours={durationHours}
              minutes={durationMinutes}
              onHoursChange={setDurationHours}
              onMinutesChange={setDurationMinutes}
            />
            <div className="flex items-end">
              <Button onClick={handleCheck} disabled={loading || !isDurationValid} className="h-10 sm:h-8 text-sm sm:text-xs w-full sm:w-auto" data-testid="check-availability-btn">
                {loading ? <Loader2 className="h-4 w-4 sm:h-3 sm:w-3 animate-spin mr-1" /> : <Zap className="h-4 w-4 sm:h-3 sm:w-3 mr-1" />}
                Check
              </Button>
            </div>
          </div>

          {/* Presets */}
          <div className="mb-3">
            <DurationPresets onSelect={handlePresetSelect} activeMinutes={totalDurationMinutes} />
          </div>

          {/* Geocoding warning */}
          {draft?.geocoding_warning && (
            <div className="p-2 rounded text-xs border border-amber-500/30 bg-amber-500/10 text-amber-400 mb-2 flex items-center gap-2" data-testid="geocoding-warning">
              <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
              <span>{draft.geocoding_warning}</span>
            </div>
          )}

          {/* No full-fit banner when near-fit exists */}
          {draft && !hasFullFit && hasNearFit && (
            <div className="p-2 rounded text-xs border border-amber-500/30 bg-amber-500/10 text-amber-400 mb-2 flex items-center gap-2" data-testid="no-full-fit-banner">
              <Timer className="h-3.5 w-3.5 flex-shrink-0" />
              <span>No full slot available for {formatDuration(totalDurationMinutes)}. Shorter alternatives found (Near Fit tab).</span>
            </div>
          )}

          {/* Results */}
          {draft && (
            <Tabs defaultValue={hasFullFit ? "fastest" : (hasNearFit ? "nearfit" : "fastest")} className="mt-2">
              <TabsList className="grid w-full h-auto min-h-[36px] grid-cols-2 sm:grid-cols-4 gap-1 p-1">
                <TabsTrigger value="fastest" className="text-[10px] sm:text-xs px-2 py-1.5" data-testid="tab-fastest">
                  <Zap className="h-3 w-3 mr-1 hidden sm:inline" />Fastest ({draft.fastest_results?.length || 0})
                </TabsTrigger>
                <TabsTrigger value="bestfit" className="text-[10px] sm:text-xs px-2 py-1.5" data-testid="tab-bestfit">
                  <TrendingDown className="h-3 w-3 mr-1 hidden sm:inline" />Best ({draft.best_fit_results?.length || 0})
                </TabsTrigger>
                {nearFitResults.length > 0 && (
                  <TabsTrigger value="nearfit" className="text-[10px] sm:text-xs px-2 py-1.5 text-orange-400 data-[state=active]:text-orange-400 data-[state=active]:bg-orange-500/10" data-testid="tab-nearfit">
                    <AlertTriangle className="h-3 w-3 mr-1 hidden sm:inline" />⚠ Короче ({nearFitResults.length})
                  </TabsTrigger>
                )}
                <TabsTrigger value="manual" className="text-[10px] sm:text-xs px-2 py-1.5" data-testid="tab-manual">
                  <User className="h-3 w-3 mr-1 hidden sm:inline" />Manual
                </TabsTrigger>
              </TabsList>

              <TabsContent value="fastest" className="mt-2 space-y-2" data-testid="fastest-results">
                {draft.fastest_results?.map((c, i) => (
                  <CandidateCard key={i} c={c} index={i} isBestFit={false} isNearFit={false}
                    onSelect={c => handleSelectCandidate(c, 'fastest')} selecting={selecting === c.slot_start + c.date} />
                ))}
                {(!draft.fastest_results || draft.fastest_results.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {hasNearFit ? 'No full-fit slots. Check Near Fit tab for shorter alternatives.' : 'No slots available'}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="bestfit" className="mt-2 space-y-2" data-testid="bestfit-results">
                {draft.best_fit_results?.map((c, i) => (
                  <CandidateCard key={i} c={c} index={i} isBestFit={true} isNearFit={false}
                    onSelect={c => handleSelectCandidate(c, 'best_fit')} selecting={selecting === c.slot_start + c.date} />
                ))}
                {(!draft.best_fit_results || draft.best_fit_results.length === 0) && (
                  <p className="text-xs text-muted-foreground text-center py-4">
                    {hasNearFit ? 'No full-fit slots. Check Near Fit tab for shorter alternatives.' : 'No best-fit slots available'}
                  </p>
                )}
              </TabsContent>

              {nearFitResults.length > 0 && (
                <TabsContent value="nearfit" className="mt-2 space-y-2" data-testid="nearfit-results">
                  <div className="p-3 rounded-lg border border-orange-500/50 bg-orange-500/10 mb-3" data-testid="nearfit-warning-banner">
                    <div className="flex items-center gap-2 text-orange-400 font-semibold text-xs mb-1">
                      <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                      Сокращённые слоты — требуют подтверждения
                    </div>
                    <p className="text-xs text-orange-400/80">
                      Идеальных окон нет. Эти слоты <b>короче</b> запрошенных {formatDuration(totalDurationMinutes)}.
                      Уборка будет выполнена частично. Выбор требует явного подтверждения менеджера.
                    </p>
                  </div>
                  {nearFitResults.map((c, i) => (
                    <CandidateCard key={i} c={c} index={i} isBestFit={false} isNearFit={true}
                      onSelect={c => handleSelectCandidate(c, 'near_fit')} selecting={selecting === c.slot_start + c.date} />
                  ))}
                </TabsContent>
              )}

              <TabsContent value="manual" className="mt-2">
                <ManualAssignPanel
                  api={api}
                  draftId={draft.draft_id}
                  workers={workers}
                  onSelected={handleManualSelected}
                />
              </TabsContent>
            </Tabs>
          )}
        </CardContent>
      </Card>

      {draft && (
        <CompleteOrderDialog
          open={completeOpen}
          onOpenChange={setCompleteOpen}
          api={api}
          draftId={draft.draft_id}
          draft={draft}
          onCompleted={handleOrderCompleted}
        />
      )}

      <NearFitConfirmDialog
        open={nearFitConfirmOpen}
        onOpenChange={setNearFitConfirmOpen}
        candidate={nearFitCandidate}
        onConfirm={handleNearFitConfirm}
        loading={nearFitLoading}
      />
    </>
  );
}
